import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { GoogleLogo } from '@/components/brand/GoogleLogo';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { tokenStorage } from '@/utils/token-storage';
import { logAuthStage } from '@/lib/auth-log';
import { passwordSchema, PASSWORD_HINT } from '@/lib/password-policy';

import { Separator } from '@/components/ui/separator';
import { EyeIcon, EyeOffIcon, UserPlusIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useSignup } from '@/hooks/use-auth';
import { useErrorHandler } from '@/hooks/use-error-handler';
import OTPVerificationModal from '@/components/OTPVerificationModal';
import { formatPhoneNumber, validatePhoneNumber } from '@/utils/phoneUtils';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { US_STATES_OPTIONS } from '@/utils/us-states';

const formSchema = z
  .object({
    accountType: z.enum(['company', 'architect', 'subcontractor']),
    companyName: z.string().optional(),
    businessEmail: z
      .string()
      .min(1, 'Email is required')
      .email('Please enter a valid email address'),
    fullName: z.string().min(1, 'Full name is required'),
    phoneNumber: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    agreeToTerms: z.boolean().refine((val) => val === true, {
      message: 'You must agree to the terms and conditions',
    }),
  })
  .superRefine((data, ctx) => {
    // Company-specific validations
    if (data.accountType === 'company') {
      if (!data.companyName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Company name is required',
          path: ['companyName'],
        });
      }
      if (!data.phoneNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone number is required',
          path: ['phoneNumber'],
        });
      } else if (!validatePhoneNumber(data.phoneNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone number must be in format (555) 123-4567',
          path: ['phoneNumber'],
        });
      }
      if (!data.state) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'State is required',
          path: ['state'],
        });
      }
      if (!data.zipCode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ZIP code is required',
          path: ['zipCode'],
        });
      } else if (!/^\d{5}$/.test(data.zipCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ZIP code must be 5 digits',
          path: ['zipCode'],
        });
      }
    }

    // Password confirmation validation
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Passwords do not match',
        path: ['confirmPassword'],
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

export default function SignupPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [selectedAccountType, setSelectedAccountType] = useState<'company' | 'architect' | 'subcontractor'>('company');

  const { toast } = useToast();
  const { handleAuthError } = useErrorHandler();
  const navigate = useNavigate();

  // TanStack Query mutation for signup
  const signupMutation = useSignup();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountType: 'company',
      companyName: '',
      businessEmail: '',
      fullName: '',
      phoneNumber: '',
      state: '',
      zipCode: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false,
    },
  });

  // Watch account type to show/hide fields
  const accountType = form.watch('accountType');

  // Clear company-specific fields when switching to non-company account types
  useEffect(() => {
    if (accountType === 'architect' || accountType === 'subcontractor') {
      form.setValue('companyName', '');
      form.setValue('phoneNumber', '');
      form.setValue('state', '');
      form.setValue('zipCode', '');
    }
  }, [accountType, form]);

  const onSubmit = async (data: FormValues) => {
    try {
      logAuthStage('signup-submit');

      // Prepare the data based on account type
      const submitData: any = {
        accountType: data.accountType,
        businessEmail: data.businessEmail,
        fullName: data.fullName,
        password: data.password,
        confirmPassword: data.confirmPassword,
        agreeToTerms: data.agreeToTerms,
      };

      // Only include company-specific fields for company accounts
      if (data.accountType === 'company') {
        submitData.companyName = data.companyName;
        submitData.phoneNumber = data.phoneNumber;
        submitData.state = data.state;
        submitData.zipCode = data.zipCode;
      }

      // Call the signup API using TanStack Query mutation
      const response = await signupMutation.mutateAsync(submitData);
      tokenStorage.clearAll();

      logAuthStage('signup-response', { success: Boolean(response) });

      toast({
        title: 'Account Created Successfully!',
        description: 'Please check your email for the verification code.',
        variant: 'success',
      });

      // Store email and account type for OTP verification and navigation
      setUserEmail(data.businessEmail);
      setSelectedAccountType(data.accountType); // Store account type before form reset
      setShowOTPModal(true);

      // Reset form after success
      form.reset();
    } catch (error: any) {
      console.error('Signup error:', error);

      // Use common error handler for consistent error messages
      handleAuthError(error);
    }
  };

  const handleGoogleSignIn = () => {
    toast({
      title: 'Google Sign-In',
      description: 'Google authentication would be implemented here.',
      variant: 'warning',
    });
  };

  const handleOTPVerificationSuccess = (userData?: any) => {
    toast({
      title: 'Email Verified Successfully!',
      description: "Welcome to larkPM! Let's set up your account.",
      variant: 'success',
    });

    // Navigate to appropriate onboarding based on stored account type (not form value)
    logAuthStage('signup-otp-verified', { accountType: selectedAccountType });

    if (userData?.organizationId) {
      if (selectedAccountType === 'company') {
        navigate(`/onboarding/profile?orgId=${userData.organizationId}`);
      } else {
        // For architect and subcontractor, use extended onboarding
        navigate(`/onboarding/business-info?orgId=${userData.organizationId}&type=${selectedAccountType}`);
      }
    }
  };

  const handleCloseOTPModal = () => {
    setShowOTPModal(false);
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <Card className="border-border">
          <CardContent className="p-20">
            {/* Logo */}
            <div className="mb-8">
              <img
                src="/logo.png"
                alt="Company Logo"
                className="h-12 w-auto object-contain"
              />
            </div>

            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold tracking-tight text-foreground mb-2">
                Let's Get Building
              </h1>
              <p className="text-muted-foreground text-sm">
                Join the platform that makes deck planning easier, faster, and
                way more accurate.
              </p>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                {/* Account Type Selection */}
                <FormField
                  control={form.control}
                  name="accountType"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className="flex flex-wrap gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="company" id="company" />
                            <Label htmlFor="company" className="text-foreground">
                              Company
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="architect" id="architect" />
                            <Label
                              htmlFor="architect"
                              className="text-foreground"
                            >
                              Architect/Draftsman
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="subcontractor"
                              id="subcontractor"
                            />
                            <Label
                              htmlFor="subcontractor"
                              className="text-foreground"
                            >
                              Sub Contractor/Partner
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Company Name - Only for Company account type */}
                {accountType === 'company' && (
                  <FormField
                    control={form.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground font-medium">
                          Company Name<span className="text-red-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="ABC Company, Inc."
                            {...field}
                            className="mt-1"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Email */}
                <FormField
                  control={form.control}
                  name="businessEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground font-medium">
                        {accountType === 'company' ? 'Business Email' : 'Email'}<span className="text-red-600">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={accountType === 'company' ? "jane@abccompany.com" : "jane@email.com"}
                          {...field}
                          className="mt-1"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Name and Phone */}
                {accountType === 'company' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="fullName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            Full Name<span className="text-red-600">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Jane Smith"
                              {...field}
                              className="mt-1"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            Phone Number<span className="text-red-600">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="(555) 123-4567"
                              {...field}
                              className="mt-1"
                              onChange={(e) => {
                                const formatted = formatPhoneNumber(
                                  e.target.value
                                );
                                field.onChange(formatted);
                              }}
                              maxLength={14}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                ) : (
                  <FormField
                    control={form.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground font-medium">
                          Full Name<span className="text-red-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Jane Smith"
                            {...field}
                            className="mt-1"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* State and ZIP - Only for Company account type */}
                {accountType === 'company' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            State<span className="text-red-600">*</span>
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {US_STATES_OPTIONS.map((state) => (
                                <SelectItem key={state.code} value={state.value}>
                                  {state.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="zipCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            ZIP Code<span className="text-red-600">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="12345"
                              {...field}
                              className="mt-1"
                              maxLength={5}
                              pattern="[0-9]*"
                              autoComplete="noe"
                              inputMode="numeric"
                              onChange={(e) => {
                                // Only allow numeric characters
                                const numericValue = e.target.value.replace(
                                  /\D/g,
                                  ''
                                );
                                field.onChange(numericValue);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* Password Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground font-medium">
                          Password<span className="text-red-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showPassword ? 'text' : 'password'}
                              placeholder="Enter password"
                              autoComplete="new-password"
                              {...field}
                              className="pr-10"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1"
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={
                                showPassword ? 'Hide password' : 'Show password'
                              }
                            >
                              {showPassword ? (
                                <EyeOffIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        {field.value && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {PASSWORD_HINT}
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground font-medium">
                          Confirm Password
                          <span className="text-red-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showConfirmPassword ? 'text' : 'password'}
                              placeholder="Confirm password"
                              autoComplete="confirm-password"
                              {...field}
                              className="pr-10"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              aria-label={
                                showConfirmPassword
                                  ? 'Hide password'
                                  : 'Show password'
                              }
                            >
                              {showConfirmPassword ? (
                                <EyeOffIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Terms and Conditions */}
                <FormField
                  control={form.control}
                  name="agreeToTerms"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-start space-x-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="mt-1"
                          />
                        </FormControl>
                        <FormLabel className="text-sm text-foreground">
                          I've read and agree with the{' '}
                          <span className="text-yellow-500 hover:underline cursor-pointer">
                            Terms & conditions
                          </span>{' '}
                          and the{' '}
                          <span className="text-yellow-500 hover:underline cursor-pointer">
                            Privacy Policy
                          </span>
                        </FormLabel>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Sign Up Button */}
                <div className="mt-8">
                  <Button
                    type="submit"
                    className="w-full bg-foreground-soft hover:bg-foreground text-primary-foreground disabled:opacity-50"
                    disabled={signupMutation.isPending}
                  >
                    {signupMutation.isPending ? (
                      <span className="flex items-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Creating Account...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        Signup
                        <UserPlusIcon className="h-4 w-4 ml-2" />
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            </Form>

            {/* Login Link */}
            <div className="mt-4 text-center text-sm">
              <span className="text-muted-foreground">
                Got an account? Let's get you{' '}
              </span>
              <Button
                type="button"
                variant="link"
                onClick={() => navigate('/login')}
                className="text-yellow-600 hover:underline p-0 h-auto text-sm"
              >
                signed in!
              </Button>
            </div>

            {/* Divider */}
            <div className="flex items-center my-6">
              <Separator className="flex-1" />
              <span className="px-4 text-muted-foreground">Or</span>
              <Separator className="flex-1" />
            </div>

            {/* Google Sign In Button */}
            <div className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 bg-card hover:bg-muted border-border"
              >
                <span className="flex items-center gap-3">
                  <GoogleLogo size={20} />
                  Sign in with Google
                </span>
              </Button>
            </div>

            {/* Success Alert */}
            {/* {showSuccessAlert && (
              <Alert className='mt-6 bg-green-50 border-green-200'>
                <CheckIcon className='h-4 w-4 text-green-600' />
                <AlertTitle className='text-green-800'>
                  Account Created Successfully!
                </AlertTitle>
                <AlertDescription className='text-green-700'>
                  You can now set up your company, team and start managing projects.
                </AlertDescription>
              </Alert>
            )} */}
          </CardContent>
        </Card>
      </div>

      {/* OTP Verification Modal */}
      <OTPVerificationModal
        isOpen={showOTPModal}
        onClose={handleCloseOTPModal}
        email={userEmail}
        onVerificationSuccess={handleOTPVerificationSuccess}
      />
    </div>
  );
}
